import { useMemo } from 'react';

import { neutralBrand } from '@/config/brands';
import { brandingConfig, resolvePartnerBrand, type BrandConfig } from '@/config/branding';
import { knownPartnerByHost } from '@/config/current-partner';
import { mergePartnerBranding } from '@/lib/branding/merge-partner-branding';
import {
  usePartnerBranding,
  usePartnerBrandingForHost,
  usePartnerBrandingStatusForHost,
} from '@/hooks/use-partner-branding';

const redirectHostFromUrl = (url: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

/** Sign-in brand: code file, then DB overlay for redirect host or current host. */
export const useResolveSignInBrand = (partnerRedirectUrl: string | null): BrandConfig => {
  const codeBrand = resolvePartnerBrand(partnerRedirectUrl);
  const redirectHost = redirectHostFromUrl(partnerRedirectUrl);
  const hostForLookup = codeBrand ? null : redirectHost;
  const redirectDb = usePartnerBrandingForHost(hostForLookup);
  const redirectStatus = usePartnerBrandingStatusForHost(hostForLookup);
  const ambientDb = usePartnerBranding();
  const dbOverlay = redirectDb ?? ambientDb;

  // A registered partner satellite whose brand comes from the DB (no code file):
  // its branding arrives async, so the redirect-host lookup can be mid-flight.
  const isDbPartnerHop = !codeBrand && !!knownPartnerByHost(redirectHost);

  return useMemo(() => {
    if (codeBrand) return codeBrand;
    // While that partner's DB branding is still loading, show a neutral (blank)
    // brand — never the tagada default — so the re-skinned sign-in never flashes
    // TagadaPay's logo/name/colour before the partner branding lands. `neutralBrand`
    // has a blank logo/appName, so `BrandLogo` renders nothing and the title effect
    // (guarded on `appName`) defers until the real brand resolves.
    if (isDbPartnerHop && redirectStatus === 'loading') return neutralBrand;
    if (dbOverlay) return mergePartnerBranding(brandingConfig, dbOverlay);
    return brandingConfig;
  }, [codeBrand, dbOverlay, isDbPartnerHop, redirectStatus]);
};
